import axios, { AxiosResponse } from 'axios';
import xorBy from 'lodash/xorBy';
import { CandidateUpdateProfileJob } from '~/utils/candidate';
import hasLangChanged from '~/utils/languages/hasLangChanged';
import AuthService from './AuthService';
import { BaseService } from './BaseService';
import IdentityService from './IdentityService';
import {
    Candidate,
    CandidateResponse,
    CareerField,
    Employer,
    Language,
    PredefinedSkill,
    ProfileVisibility,
    Sector,
    transformCandidate,
    transformPredefinedSkills,
    transformWorkAuthorization,
    WorkAuthorization,
    WorkAuthorizationResponse,
} from './types/candidate';

export * from './types/candidate';

export default class CandidateService extends BaseService {
    constructor() {
        super('candidate');
    }

    async getMe() {
        return axios({
            url: this.getUrl('me', {
                include: [
                    'summary',
                    'skills',
                    'preferences',
                    'work_experiences',
                    'links',
                    'memberships',
                    'nationalities',
                    'emailsettings',
                ],
            }),
        }).then((res: AxiosResponse<CandidateResponse>) => transformCandidate(res.data));
    }

    async createSchool(name: string) {
        return axios({
            url: this.getUrl('school'),
            method: 'POST',
            data: { name },
        }).then((res: AxiosResponse<{ id: number }>) => res.data);
    }

    /* WorkAuthorizations */

    async getWorkAuthorizations() {
        return axios({
            url: this.getUrl('workauthorization'),
        }).then((res: AxiosResponse<WorkAuthorizationResponse[]>) =>
            res.data.map(transformWorkAuthorization)
        );
    }

    async createWorkAuthorization(workAuthorization: WorkAuthorization) {
        return axios({
            url: this.getUrl('workauthorization'),
            method: 'POST',
            data: workAuthorization,
        }).then(this.isOK);
    }
    async deleteWorkAuthorization(id: string) {
        return axios({
            url: this.getUrl(`workauthorization/${id}`),
            method: 'DELETE',
        }).then(this.isOK);
    }

    async saveWorkAuthorizations(
        workAuthorizations: WorkAuthorization[],
        oldWordAuthorizations: WorkAuthorization[]
    ) {
        const errors: string[] = [];

        const wa = workAuthorizations.filter(wa => !wa.implicit);
        const waOld = oldWordAuthorizations.filter(wa => !wa.implicit);

        const waAdd = wa.reduce<WorkAuthorization[]>(
            (acc, cur) =>
                cur.implicit || waOld.some(wa => wa.code === cur.code && wa.type === cur.type)
                    ? acc
                    : [...acc, cur],
            []
        );
        const waDelete = xorBy(wa.filter(w => !!w.id), waOld, 'id');

        for await (const wa of waAdd)
            this.createWorkAuthorization(wa).catch(err => errors.push(err));

        for await (const wa of waDelete)
            this.deleteWorkAuthorization(wa.id).catch(err => errors.push(err));

        return !errors.length ? Promise.resolve(true) : Promise.reject(errors);
    }

    /* Summary */

    async saveSummary(summary: string) {
        return axios({
            url: this.getUrl('me'),
            method: 'PUT',
            data: { summary },
        }).then(this.isOK);
    }

    async saveMe(data: Partial<Candidate>) {
        return axios({
            url: this.getUrl('me'),
            method: 'PUT',
            data: data,
        }).then(this.isOK);
    }

    /* Languages */

    async createLanguage(language: Language) {
        return axios({
            url: this.getUrl('language'),
            method: 'POST',
            data: {
                languageId: language.languageId,
                proficiencyId: language.proficiencyId,
            },
        }).then(this.isOK);
    }
    async updateLanguage(language: Language) {
        return axios({
            url: this.getUrl(`language/${language.id}`),
            method: 'PUT',
            data: {
                languageId: language.languageId,
                proficiencyId: language.proficiencyId,
            },
        }).then(this.isOK);
    }
    async deleteLanguage(id: string) {
        return axios({
            url: this.getUrl(`language/${id}`),
            method: 'DELETE',
        }).then(this.isOK);
    }

    async saveLanguages(languages: Language[], oldLanguages: Language[]) {
        const errors: string[] = [];

        for await (const lang of languages) {
            const hasChanged = hasLangChanged(lang, oldLanguages);

            if (lang.id && hasChanged)
                await this.updateLanguage(lang).catch(err => errors.push(err));
            else if (!lang.id && lang.languageId && lang.proficiencyId)
                await this.createLanguage(lang).catch(err => errors.push(err));
        }

        const langDeleted = xorBy(languages.filter(l => !!l.id), oldLanguages, 'id');
        for await (const lang of langDeleted) await this.deleteLanguage(lang.id);

        return !errors.length ? Promise.resolve(true) : Promise.reject(errors);
    }

    /* Skills */
    async getAllSkills() {
        return axios({
            url: this.getUrl('skills'),
        }).then((res: AxiosResponse<{ results: PredefinedSkill[] }>) =>
            transformPredefinedSkills(res.data)
        );
    }

    /* Sectors */
    async getAllSectors() {
        return axios({
            url: this.getUrl('sectors'),
        }).then((res: AxiosResponse<{ results: Sector[] }>) => res.data.results);
    }

    /* Career Fields */
    async getAllCareerFields() {
        return axios({
            url: this.getUrl('careerfields'),
        }).then((res: AxiosResponse<{ results: CareerField[] }>) => res.data.results);
    }

    /* Employers */
    async getAllEmployers(includeCustomByCandidateId?: string) {
        const params: { includeCustomFor?: string } = {};
        if (includeCustomByCandidateId) {
            params.includeCustomFor = includeCustomByCandidateId;
        }

        return axios({
            url: this.getUrl('employers'),
            params,
        }).then((res: AxiosResponse<{ results: Employer[] }>) => res.data.results);
    }

    /* Profile */

    async updateCandidateProfile(
        request: CandidateUpdateProfileJob,
        newAvatar: File | null = null
    ) {
        // Handle avatar update
        if (!!newAvatar) {
            const service = new IdentityService();
            await service.saveImage(newAvatar);
        }

        return axios({
            url: this.getUrl('profile'),
            method: 'POST',
            data: request,
        }).then(res => {
            // Emit custom event 'candidate:profile:update'
            const event = new CustomEvent('candidate:profile:update', {
                detail: request,
            });
            window.dispatchEvent(event);

            return this.isOK(res);
        });
    }

    async setProfileVisibility(visibility: ProfileVisibility) {
        return axios({
            url: this.getUrl('profile'),
            method: 'POST',
            data: {
                userId: await AuthService.getUserId(),
                candidate: {
                    op: 'update',
                    value: {
                        profileVisibility: visibility,
                    },
                },
            } as CandidateUpdateProfileJob,
        }).then(this.isOK);
    }
}
